import { useEffect, useRef, useState } from 'react';
import { trpc } from '../../lib/trpc';
import { Button } from '../../components/ui/Button';
import { Typography } from '../../components/ui/Typography';
import { emailConfirmMatches } from './accountDeletionCopy';

interface PurgeUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userEmail: string;
  onPurged: () => void;
}

const PurgeUserDialogBody = ({
  onClose,
  userId,
  userEmail,
  onPurged,
}: Omit<PurgeUserDialogProps, 'isOpen'>) => {
  const [confirm, setConfirm] = useState('');
  const dialogRef = useRef<HTMLDivElement>(null);

  const purge = trpc.adminUsers.purge.useMutation({ onSuccess: onPurged });
  const isPending = purge.isPending;

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isPending) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isPending, onClose]);

  const isReady = emailConfirmMatches(confirm, userEmail);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isPending) onClose();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label="Purge account"
        className="bg-gray-50 rounded-lg shadow-xl max-w-md w-full"
      >
        <div className="p-6 border-b border-gray-200 flex items-center justify-between gap-3">
          <Typography variant="heading3" as="h3">
            Purge account
          </Typography>
          <div className="flex items-center gap-3">
            {purge.error && (
              <span role="alert" className="text-sm text-error">
                {purge.error.message}
              </span>
            )}
            <Button variant="outline" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => isReady && purge.mutate({ id: userId, confirm })}
              disabled={!isReady}
              loading={isPending}
            >
              {isPending ? 'Purging…' : 'Purge now'}
            </Button>
          </div>
        </div>
        <div className="p-6 space-y-5">
          <p className="text-gray-700">
            This permanently deletes {userEmail} and everything they&apos;ve posted. It can&apos;t
            be undone.
          </p>
          <div>
            <label htmlFor="purge-confirm" className="block text-sm font-medium text-gray-700 mb-1">
              Type {userEmail} to confirm
            </label>
            <input
              id="purge-confirm"
              type="text"
              value={confirm}
              autoComplete="off"
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-info-500 focus:ring-1 focus:ring-info-500 outline-none"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/** Mounted only while open, so the typed email and errors reset on close. */
export const PurgeUserDialog = ({ isOpen, ...rest }: PurgeUserDialogProps) =>
  isOpen ? <PurgeUserDialogBody {...rest} /> : null;
